import React, { useState, useMemo } from "react";
import { Layout, Typography, Card, Row, Col, Button, Tag, Input, Pagination } from "antd";
import { SearchOutlined } from "@ant-design/icons";

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const caseStudies = [
  {
    id: 1,
    title: "AI-Powered Healthcare Diagnostics",
    type: "AI Case Study",
    description: "How machine learning algorithms were applied to diagnose medical conditions from X-ray images, reducing diagnostic errors.",
    link: "#",
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  {
    id: 2,
    title: "Data Science for Predictive Maintenance in Manufacturing",
    type: "Data Science Case Study",
    description: "A case study where predictive maintenance techniques were used to predict equipment failures in a manufacturing plant.",
    link: "#",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  {
    id: 3,
    title: "Cybersecurity Threat Detection Using AI",
    type: "Cybersecurity Case Study",
    description: "Exploring the implementation of AI algorithms to detect and prevent potential cybersecurity threats in real-time.",
    link: "#",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80", // Updated working URL
  },
  {
    id: 4,
    title: "AI in Financial Fraud Detection",
    type: "AI Case Study",
    description: "How AI was implemented to detect fraudulent activities in financial transactions, improving security and reducing losses.",
    link: "#",
    image: "https://images.unsplash.com/photo-1551288049-bbfda027e32b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80", // Updated working URL
  },
  {
    id: 5,
    title: "Real-Time Data Analysis for Social Media Insights",
    type: "Data Science Case Study",
    description: "Analyzing real-time social media data to gather insights on customer sentiment, improving marketing strategies.",
    link: "#",
    image: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  {
    id: 6,
    title: "Cybersecurity Incident Response Automation",
    type: "Cybersecurity Case Study",
    description: "How automation tools were used to reduce response time to cybersecurity incidents, improving operational efficiency.",
    link: "#",
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  // Additional case studies to demonstrate pagination
  {
    id: 7,
    title: "AI-Driven Customer Support Enhancement",
    type: "AI Case Study",
    description: "Using AI chatbots to improve customer support response times and satisfaction in a retail company.",
    link: "#",
    image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  {
    id: 8,
    title: "Data Science in Supply Chain Optimization",
    type: "Data Science Case Study",
    description: "Leveraging data analytics to optimize supply chain logistics and reduce costs in a global firm.",
    link: "#",
    image: "https://images.unsplash.com/photo-1586528116311-01c1dedfca63?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  {
    id: 9,
    title: "AI for Traffic Management",
    type: "AI Case Study",
    description: "Implementing AI to manage urban traffic flow, reducing congestion in a major city.",
    link: "#",
    image: "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
  {
    id: 10,
    title: "Cybersecurity for IoT Devices",
    type: "Cybersecurity Case Study",
    description: "Securing IoT devices in smart homes using advanced AI-based threat detection.",
    link: "#",
    image: "https://images.unsplash.com/photo-1554995207-c18c203602cb?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
  },
];

const CaseStudies = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  const filteredCaseStudies = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return caseStudies.filter(
      (study) =>
        study.title.toLowerCase().includes(query) ||
        study.description.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const paginatedCaseStudies = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredCaseStudies.slice(startIndex, startIndex + pageSize);
  }, [filteredCaseStudies, currentPage]);

  return (
    <Layout>
      <Content style={{ padding: "50px", background: "#f0f2f5" }}>
        <Title level={2} style={{ fontSize: "24px", color: "#1890ff" }}>
          Case Studies
        </Title>
        <Paragraph style={{ fontSize: "16px", color: "#666", marginBottom: "24px" }}>
          Our case studies showcase how AI, Cybersecurity, and Data Science are revolutionizing industries. Dive into these real-world examples of how technology is being applied to solve complex problems and improve operational efficiency.
        </Paragraph>

        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={24} md={12}>
            <Input
              placeholder="Search case studies by title or description"
              prefix={<SearchOutlined />}
              size="large"
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ borderRadius: "4px" }}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          {paginatedCaseStudies.length > 0 ? (
            paginatedCaseStudies.map((study) => (
              <Col xs={24} sm={12} md={8} lg={6} key={study.id}>
                <Card
                  hoverable
                  cover={
                    <img
                      alt={study.title}
                      src={study.image}
                      style={{ height: "150px", objectFit: "cover" }}
                    />
                  }
                  style={{
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  bodyStyle={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}
                >
                  <div>
                    <Title level={4} style={{ marginBottom: "8px", color: "#333" }}>
                      {study.title}
                    </Title>
                    <Tag color="green" style={{ marginBottom: "12px" }}>
                      {study.type}
                    </Tag>
                    <Paragraph style={{ color: "#666", fontSize: "14px" }}>
                      {study.description}
                    </Paragraph>
                  </div>
                  <Button
                    type="primary"
                    href={study.link}
                    target="_blank"
                    style={{ backgroundColor: "#1890ff", borderColor: "#1890ff", width: "100%", marginTop: "16px" }}
                  >
                    Read More
                  </Button>
                </Card>
              </Col>
            ))
          ) : (
            <Col span={24} style={{ textAlign: "center" }}>
              <Paragraph style={{ color: "#999", fontSize: "16px" }}>
                No case studies match your search. Try a different query!
              </Paragraph>
            </Col>
          )}
        </Row>

        <Pagination
          current={currentPage}
          total={filteredCaseStudies.length}
          pageSize={pageSize}
          onChange={setCurrentPage}
          style={{ marginTop: "24px", textAlign: "center" }}
          showSizeChanger={false}
        />
      </Content>
    </Layout>
  );
};

export default CaseStudies;